/**
 * Persists appointment list filters (calendar, flowboard, grooming) in localStorage.
 */

const CALENDAR_FILTERS_KEY = "calendar-appointments-filters-v1";
const FLOWBOARD_FILTERS_KEY = "flowboard-appointments-filters-v1";
const GROOMING_FILTERS_KEY = "grooming-appointments-filters-v1";

function safeString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function safeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function safeBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

const DRIVER_TYPE_SET = new Set(["pickup", "dropoff"]);

function safeDriverTypes(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const v of value) {
    if (typeof v === "string" && DRIVER_TYPE_SET.has(v) && !out.includes(v)) {
      out.push(v);
    }
  }
  return out;
}

export type PersistedCalendarFilters = {
  searchTerm: string;
  selectedVets: string[];
  selectedTypes: string[];
  selectedStatuses: string[];
  selectedDriverTypes: string[];
};

const DEFAULT_CALENDAR_FILTERS: PersistedCalendarFilters = {
  searchTerm: "",
  selectedVets: [],
  selectedTypes: [],
  selectedStatuses: [],
  selectedDriverTypes: [],
};

export function readPersistedCalendarFilters(): PersistedCalendarFilters {
  try {
    const raw = localStorage.getItem(CALENDAR_FILTERS_KEY);
    if (!raw) return DEFAULT_CALENDAR_FILTERS;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return DEFAULT_CALENDAR_FILTERS;
    const o = parsed as Record<string, unknown>;
    return {
      searchTerm: safeString(o.searchTerm),
      selectedVets: safeStringArray(o.selectedVets),
      selectedTypes: safeStringArray(o.selectedTypes),
      selectedStatuses: safeStringArray(o.selectedStatuses),
      selectedDriverTypes: safeDriverTypes(o.selectedDriverTypes),
    };
  } catch {
    return DEFAULT_CALENDAR_FILTERS;
  }
}

export function persistCalendarFilters(state: PersistedCalendarFilters): void {
  try {
    localStorage.setItem(CALENDAR_FILTERS_KEY, JSON.stringify(state));
  } catch {
    // Ignore (e.g. localStorage blocked)
  }
}

export type PersistedFlowboardFilters = {
  searchTerm: string;
  selectedVets: string[];
  selectedTypes: string[];
  selectedDriverTypes: string[];
};

const DEFAULT_FLOWBOARD_FILTERS: PersistedFlowboardFilters = {
  searchTerm: "",
  selectedVets: [],
  selectedTypes: [],
  selectedDriverTypes: [],
};

export function readPersistedFlowboardFilters(): PersistedFlowboardFilters {
  try {
    const raw = localStorage.getItem(FLOWBOARD_FILTERS_KEY);
    if (!raw) return DEFAULT_FLOWBOARD_FILTERS;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return DEFAULT_FLOWBOARD_FILTERS;
    const o = parsed as Record<string, unknown>;
    return {
      searchTerm: safeString(o.searchTerm),
      selectedVets: safeStringArray(o.selectedVets),
      selectedTypes: safeStringArray(o.selectedTypes),
      selectedDriverTypes: safeDriverTypes(o.selectedDriverTypes),
    };
  } catch {
    return DEFAULT_FLOWBOARD_FILTERS;
  }
}

export function persistFlowboardFilters(state: PersistedFlowboardFilters): void {
  try {
    localStorage.setItem(FLOWBOARD_FILTERS_KEY, JSON.stringify(state));
  } catch {
    // Ignore
  }
}

export type PersistedGroomingFilters = {
  searchTerm: string;
  selectedVets: string[];
  onlyTaxiDog: boolean;
};

const DEFAULT_GROOMING_FILTERS: PersistedGroomingFilters = {
  searchTerm: "",
  selectedVets: [],
  onlyTaxiDog: false,
};

export function readPersistedGroomingFilters(): PersistedGroomingFilters {
  try {
    const raw = localStorage.getItem(GROOMING_FILTERS_KEY);
    if (!raw) return DEFAULT_GROOMING_FILTERS;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return DEFAULT_GROOMING_FILTERS;
    const o = parsed as Record<string, unknown>;
    return {
      searchTerm: safeString(o.searchTerm),
      selectedVets: safeStringArray(o.selectedVets),
      onlyTaxiDog: safeBoolean(o.onlyTaxiDog),
    };
  } catch {
    return DEFAULT_GROOMING_FILTERS;
  }
}

export function persistGroomingFilters(state: PersistedGroomingFilters): void {
  try {
    localStorage.setItem(GROOMING_FILTERS_KEY, JSON.stringify(state));
  } catch {
    // Ignore
  }
}
